/**
 * The reward figure a promo card leads with, in the reader's language.
 *
 * It lived inside `PromoCard` while the lobby row card next door rendered a
 * catalogue-free helper out of the data layer — so the first promo headline a
 * player ever saw, on the lobby, was English whatever language they had picked
 * (#362). One module, both cards, one money formatter.
 */
import type { TFunction } from 'i18next';
import { formatMoney } from '@/lib/money/index.js';
import { toBigOrNull as toBig } from './reward-rules.js';
import type { Promotion } from './api.js';

/**
 * Headline for a promotion whose kind the card knows how to price. `money`
 * formats the config's LEDGER minor units; a screen passes its display
 * formatter, and it defaults to plain `formatMoney` in `currency`.
 */
export function rewardHeadlineFor(
  promo: Promotion,
  currency: string,
  t: TFunction,
  money: (minor: bigint) => string = (m) => formatMoney(m, currency),
): string {
  const r = (promo.rewardRules ?? {}) as Record<string, unknown>;
  switch (promo.kind) {
    case 'welcome': {
      const amount = toBig(r['amount']);
      if (amount !== null) {
        return t('card.headline.welcome', { amount: money(amount) });
      }
      break;
    }
    case 'first_deposit':
    case 'reload': {
      const pct = r['pct'] ?? '?';
      const capMinor = toBig(r['cap']);
      // The capped form is its own key, not a suffix glued onto the plain one:
      // a language that puts the cap before the percentage cannot reorder a
      // sentence that was already assembled here.
      return capMinor !== null
        ? t('card.headline.percentCapped', { pct, cap: money(capMinor) })
        : t('card.headline.percent', { pct });
    }
    case 'cashback':
      // No period word here: the headline has no room to say which one, and
      // naming the wrong one is what this card got wrong before.
      if (r['pct'] != null) return t('card.headline.cashback', { pct: r['pct'] });
      if (r['defaultBips'] != null) {
        return t('card.headline.cashback', { pct: Number(r['defaultBips']) / 100 });
      }
      break;
    case 'turnover_rebate': {
      const bips = Number(r['defaultBips'] ?? 0);
      if (bips > 0) return t('card.headline.turnover_rebate', { pct: bips / 100 });
      break;
    }
    case 'daily_checkin':
      if (Array.isArray(r['amounts']) && (r['amounts'] as unknown[]).length === 7) {
        const amounts = r['amounts'] as unknown[];
        const loMinor = toBig(amounts[0]);
        const hiMinor = toBig(amounts[6]);
        if (loMinor !== null && hiMinor !== null) {
          return t('card.headline.daily_checkin', {
            lo: money(loMinor),
            hi: money(hiMinor),
          });
        }
      }
      break;
  }
  return genericRewardHeadline(promo, currency, t, money);
}

/**
 * Last resort: whatever reward numbers the config happens to carry, for a kind
 * with no branch above or one whose own fields are missing. It reads the legacy
 * `bonusType`/`bonusValue` pair too, and reuses the same catalogue sentences —
 * a headline that reached this far still has to be readable.
 */
export function genericRewardHeadline(
  promo: Promotion,
  currency: string,
  t: TFunction,
  money: (minor: bigint) => string = (m) => formatMoney(m, currency),
): string {
  const r = (promo.rewardRules ?? {}) as Record<string, unknown>;

  const amounts = Array.isArray(r['amounts']) ? (r['amounts'] as unknown[]) : [];
  if (amounts.length > 0) {
    const loMinor = toBig(amounts[0]);
    const hiMinor = toBig(amounts[amounts.length - 1]);
    if (loMinor !== null && hiMinor !== null) {
      return t('card.headline.daily_checkin', {
        lo: money(loMinor),
        hi: money(hiMinor),
      });
    }
  }

  const pct =
    numeric(r['percent']) ??
    numeric(r['pct']) ??
    (promo.bonusType === 'percent' ? promo.bonusValue : undefined);
  if (pct != null) {
    const capMinor = toBig(r['maxAmount']) ?? toBig(r['cap']);
    return capMinor !== null && capMinor > 0n
      ? t('card.headline.percentCapped', { pct, cap: money(capMinor) })
      : t('card.headline.percent', { pct });
  }

  const amountMinor = toBig(r['amount']) ?? toBig(promo.bonusValue);
  return amountMinor !== null && amountMinor !== 0n ? money(amountMinor) : '—';
}

/** Coerce a JSON reward rate (number, numeric string, or absent) to a number. */
function numeric(v: unknown): number | undefined {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v);
  return undefined;
}
